import React, { useEffect, useRef, useState, useMemo } from 'react';
import styled from 'styled-components';
import Container from 'ls-common-client/dist/components/Container';
import Text from 'ls-common-client/dist/components/Text';
import classNames from 'classnames';
import useOnScreen from '../../../hooks/useOnScreen';
import { Star10 } from '../../../assets/stars';

const StyledRoot = styled(Container)`
  @media (max-width: 1024px) {
    margin: 20px 0 0;
  }
`;

const StyledItem = styled(Container)`
  display: flex;
  align-items: center;
  height: 24px;
`;

const StyledName = styled(Container)`
  width: 25px;
  margin-right: 5px;
`;

const StyledPercentage = styled(Container)`
  width: 34px;
  min-width: 34px;
  color: ${props => props.theme.text.text300};
`;

const StyledBar = styled(Container)`
  width: 100%;
  max-width: calc(100% - 108px);
  margin-right: 20px;
  border-radius: 6px;
  background-color: #ececec;
  height: 11px;
  position: relative;
  min-width: 260px;
  @media (max-width: 1200px) {
    min-width: 150px;
  }
  @media (max-width: 1024px) {
    max-width: calc(100% - 120px);
  }
`;

const StyledBarLine = styled(Text)`
  display: block;
  height: 100%;
  border-radius: 6px;
  background: linear-gradient(to right, #40a499 50%, #ececec 50%);
  background-size: 200% 100%;
  transition: all 1s ease-out;
`;

const RatingAverage = ({ reviewRatings }) => {
  const [viewed, setViewed] = useState(false);
  const ref = useRef();
  const onScreen = useOnScreen(ref);

  useEffect(() => {
    if (onScreen) setViewed(true);
    return () => {};
  }, [onScreen]);

  const reviewStats = useMemo(() => {
    if (reviewRatings) {
      // Maps to correct data structure by calculating total and initiating fields not available
      const totalReviews = reviewRatings.reduce((agg, rating) => {
        return agg + rating.count;
      }, 0);
      return new Array(5)
        .fill(0)
        .map((_, i) => {
          const [existingRating] = reviewRatings.filter(
            ({ rating }) => rating === i + 1
          );
          return {
            rating: i + 1,
            count: existingRating ? existingRating.count : 0,
            percentage: existingRating
              ? `${Math.round((existingRating.count / totalReviews) * 100)}%`
              : '0%',
          };
        })
        .reverse();
    }
    return [];
  }, [reviewRatings]);

  return (
    <StyledRoot
      className={classNames('RatingAverage__root', {
        'RatingAverage__root--onScreen': viewed,
      })}
      ref={ref}
    >
      {reviewStats.map(({ percentage, rating }) => (
        <StyledItem className="RatingAverage__item" key={rating}>
          <StyledName className="RatingAverage__name">
            <Text
              fontSize="15px"
              width="10px"
              display="inline-block"
              textAlign="center"
            >
              {rating}
            </Text>{' '}
            <Star10 fill="#c4c4c4" width={11} />
          </StyledName>
          <StyledBar className="RatingAverage__bar">
            <StyledBarLine
              className="RatingAverage__barLine"
              style={{ width: percentage }}
            />
          </StyledBar>
          <StyledPercentage className="RatingAverage__percentage">
            {percentage}
          </StyledPercentage>
        </StyledItem>
      ))}
    </StyledRoot>
  );
};

export default RatingAverage;
